import { useState, useEffect } from "react";
import AuthService from "../services/AuthService";

const LoginPage = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check if already logged in on component mount
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      onLoginSuccess();
    }
  }, [onLoginSuccess]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password.trim()) {
      newErrors.password = "Parola este obligatorie";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate loading time for better UX
      await new Promise((resolve) => setTimeout(resolve, 500));

      const isValidLogin = AuthService.login(formData.password);

      if (isValidLogin) {
        // Set up automatic session extension
        AuthService.setupAutoSessionExtension();
        onLoginSuccess();
      } else {
        setErrors({ password: "Parola introdusa este incorècta" });
      }
    } catch (error) {
      // Handle rate limiting and other errors
      setErrors({
        general: error.message || "A aparut o eroare. Incercati din nou.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit(e);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "40px",
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
          width: "100%",
          maxWidth: "400px",
          position: "relative",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "30px" }}>
          <h1
            style={{
              color: "#333",
              marginBottom: "8px",
              fontSize: "28px",
              fontWeight: "600",
            }}
          >
            Autentificare
          </h1>
          <p style={{ color: "#666", margin: 0, fontSize: "14px" }}>
            Introduceti parola pentru a accesa aplicatia
          </p>
        </div>

        {/* General Error */}
        {errors.general && (
          <div
            style={{
              backgroundColor: "#fee",
              color: "#c33",
              padding: "12px",
              borderRadius: "6px",
              marginBottom: "20px",
              border: "1px solid #fcc",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            {errors.general}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "25px" }}>
            <label
              htmlFor="password"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#333",
                fontSize: "14px",
              }}
            >
              Parola de acces
            </label>

            <div style={{ position: "relative" }}>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Introduceti parola"
                disabled={isLoading}
                style={{
                  width: "100%",
                  padding: "12px 45px 12px 12px",
                  border: `2px solid ${errors.password ? "#e74c3c" : "#ddd"}`,
                  borderRadius: "6px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  backgroundColor: isLoading ? "#f5f5f5" : "white",
                }}
                onFocus={(e) => {
                  if (!errors.password) {
                    e.target.style.borderColor = "#007bff";
                  }
                }}
                onBlur={(e) => {
                  if (!errors.password) {
                    e.target.style.borderColor = "#ddd";
                  }
                }}
                autoFocus
              />

              {/* Show/Hide Password Button */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: isLoading ? "default" : "pointer",
                  color: "#666",
                  fontSize: "11px",
                  padding: "5px",
                  fontWeight: "500",
                  textTransform: "uppercase",
                }}
                title={showPassword ? "Ascunde parola" : "Arata parola"}
              >
                {showPassword ? "HIDE" : "SHOW"}
              </button>
            </div>

            {errors.password && (
              <div
                style={{
                  color: "#e74c3c",
                  fontSize: "12px",
                  marginTop: "5px",
                  fontStyle: "italic",
                }}
              >
                {errors.password}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: isLoading ? "#ccc" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: isLoading ? "default" : "pointer",
              transition: "background-color 0.2s ease",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = "#0056b3";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = "#007bff";
              }
            }}
          >
            {isLoading ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid transparent",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "8px",
                  }}
                ></span>
                Se conecteaza...
              </span>
            ) : (
              "Conectare"
            )}
          </button>
        </form>

        {/* Help Text */}
        <div
          style={{
            textAlign: "center",
            marginTop: "20px",
            fontSize: "12px",
            color: "#999",
          }}
        >
          Folositi tasta Enter pentru conectare rapida
        </div>

        {/* Loading Spinner Animation */}
        <style jsx>{`
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default LoginPage;
